import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import {
	getThirdPartyTypes,
	type UseFetchConfigAnalytics,
	useThirdPartyConfig,
} from '@atlassian/rovo-connectors-config/hooks';
import {
	get3pSearchConfig,
	update3pConfigCacheEntry,
	type TSearch3pConfigAPIResponse,
} from '@atlassian/search-client/fetch-search-config';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from '../__types__/RemoteObject.ts';

export type ThirdPartyAuthenticationResult = {
	authenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	isLoading: boolean;
	refetch: () => Promise<void>;
	markOptimisticallyAuthenticated: (product: ExternalProductConnection) => void;
};

const SPACE_CURATION_3P_AUTH = 'spaceCurationThirdPartyAuth';

const CONNECTION_PRODUCT_KEYS: Partial<Record<ExternalProductConnection, ProductKeys3P[]>> = {
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: [ProductKeys3P.GoogleDrive],
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: [ProductKeys3P.OneDrive, ProductKeys3P.Sharepoint],
};

const connectionProductKeySet = (product: ExternalProductConnection): Set<string> | undefined => {
	const productKeys = CONNECTION_PRODUCT_KEYS[product];
	return productKeys ? new Set<string>(productKeys) : undefined;
};

const resourceBelongsToConnection = (
	resourceType: string,
	productKeySet: Set<string>,
	resourceTypeToProductKey: Record<string, string>,
): boolean => {
	const productKey = resourceTypeToProductKey[resourceType];
	return productKey !== undefined && productKeySet.has(productKey);
};

type UseFetch3PAuthenticationsProps = {
	products: ExternalProductConnection[];
	cloudId: string;
};

export const useFetch3PAuthentications = ({
	products,
	cloudId,
}: UseFetch3PAuthenticationsProps): ThirdPartyAuthenticationResult => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [optimisticallyAuthenticatedProducts, setOptimisticallyAuthenticatedProducts] = useState<
		Set<ExternalProductConnection>
	>(new Set());

	const [revalidation, setRevalidation] = useState<{
		done: boolean;
		confirmed: Set<ExternalProductConnection>;
	}>({ done: false, confirmed: new Set() });

	const hasStartedRevalidationRef = useRef(false);

	const productsRef = useRef(products);
	productsRef.current = products;

	const thirdPartyTypesRef = useRef<Promise<Record<string, string>> | null>(null);
	const resolveThirdPartyTypes = useCallback(() => {
		if (!thirdPartyTypesRef.current) {
			thirdPartyTypesRef.current = getThirdPartyTypes();
		}
		return thirdPartyTypesRef.current;
	}, []);

	const fireAnalyticsEvent: UseFetchConfigAnalytics['fireAnalyticsEvent'] = useCallback(
		(event) => createAnalyticsEvent(event).fire(),
		[createAnalyticsEvent],
	);

	const logException: UseFetchConfigAnalytics['logException'] = useCallback((exception) => {
		getMonitoringClient().submitError(exception, {
			attribution: Attribution.INTEGRATIONS,
			partialFailure: true,
		});
	}, []);

	const analytics: UseFetchConfigAnalytics = useMemo(
		() => ({
			searchSessionId: SPACE_CURATION_3P_AUTH,
			source: SPACE_CURATION_3P_AUTH,
			actionSubject: SPACE_CURATION_3P_AUTH,
			taskNamePrefix: SPACE_CURATION_3P_AUTH,
			fireAnalyticsEvent,
			logException,
		}),
		[fireAnalyticsEvent, logException],
	);

	const disabled = !cloudId || !userId || products.length === 0;
	const { thirdPartyConfigs } = useThirdPartyConfig({
		cloudId,
		userId: userId ?? undefined,
		disabled,
		analytics,
	});

	const markOptimisticallyAuthenticated = useCallback(
		(product: ExternalProductConnection) => {
			setOptimisticallyAuthenticatedProducts((prev) => {
				const next = new Set(prev);
				next.add(product);
				return next;
			});

			const productKeySet = connectionProductKeySet(product);
			if (userId && cloudId && productKeySet) {
				void resolveThirdPartyTypes().then((resourceTypeToProductKey) =>
					update3pConfigCacheEntry(userId, cloudId, (config: TSearch3pConfigAPIResponse) =>
						config.map((resource) =>
							resourceBelongsToConnection(resource.type, productKeySet, resourceTypeToProductKey)
								? { ...resource, isUserOAuthed: true }
								: resource,
						),
					),
				);
			}
		},
		[cloudId, userId, resolveThirdPartyTypes],
	);

	const { authenticatedServices, unauthenticatedServices } = useMemo(() => {
		const authenticated: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};
		const unauthenticated: Partial<Record<ExternalProductConnection, AuthenticationStatus>> = {};

		for (const product of products) {
			const productKeys = CONNECTION_PRODUCT_KEYS[product];
			if (!productKeys) {
				continue;
			}

			let hasConfig = false;
			let isAuthenticated = false;
			let outboundAuthUrl: string | undefined;
			let icon: AuthenticationStatus['icon'];

			// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
			for (const productKey of productKeys) {
				const config = thirdPartyConfigs?.[productKey];
				if (!config) {
					continue;
				}
				if (!hasConfig) {
					icon = config.icon;
					hasConfig = true;
				}
				if (!config.userNeedsOAuth) {
					isAuthenticated = true;
				}
				if (outboundAuthUrl === undefined && config.outboundAuthUrl) {
					outboundAuthUrl = config.outboundAuthUrl;
				}
			}

			if (!hasConfig) {
				continue;
			}

			const connected =
				isAuthenticated ||
				optimisticallyAuthenticatedProducts.has(product) ||
				revalidation.confirmed.has(product);
			const status: AuthenticationStatus = { isAuthenticated: connected, outboundAuthUrl, icon };

			if (connected) {
				authenticated[product] = status;
			} else {
				unauthenticated[product] = status;
			}
		}

		return { authenticatedServices: authenticated, unauthenticatedServices: unauthenticated };
	}, [products, thirdPartyConfigs, optimisticallyAuthenticatedProducts, revalidation]);

	const runRevalidation = useCallback(
		async (options?: { skipFetch?: boolean }) => {
			const confirmed = new Set<ExternalProductConnection>();

			if (!options?.skipFetch && cloudId && userId) {
				try {
					const [resources, resourceTypeToProductKey] = await Promise.all([
						get3pSearchConfig({
							cloudId,
							userId,
							returnExpired: false,
							onlyRefreshIfExpired: false,
						}),
						resolveThirdPartyTypes(),
					]);

					for (const product of productsRef.current) {
						const productKeySet = connectionProductKeySet(product);
						if (!productKeySet) {
							continue;
						}
						// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
						const isConnected = resources?.some(
							(resource) =>
								resourceBelongsToConnection(
									resource.type,
									productKeySet,
									resourceTypeToProductKey,
								) && resource.isUserOAuthed,
						);
						if (isConnected) {
							confirmed.add(product);
						}
					}
				} catch {}
			}

			setRevalidation((prev) => {
				const nextConfirmed = new Set(prev.confirmed);
				confirmed.forEach((product) => nextConfirmed.add(product));
				return { done: true, confirmed: nextConfirmed };
			});
		},
		[cloudId, userId, resolveThirdPartyTypes],
	);

	useEffect(() => {
		if (hasStartedRevalidationRef.current) {
			return;
		}
		if (disabled || thirdPartyConfigs === undefined) {
			return;
		}
		hasStartedRevalidationRef.current = true;
		const skipFetch = Object.keys(unauthenticatedServices).length === 0;
		void runRevalidation({ skipFetch });
	}, [disabled, thirdPartyConfigs, unauthenticatedServices, runRevalidation]);

	const refetch = useCallback(async () => {
		await runRevalidation();
	}, [runRevalidation]);

	const configLoading = !disabled && thirdPartyConfigs === undefined;
	const wouldShowAuthCard = !configLoading && Object.keys(unauthenticatedServices).length > 0;
	const isLoading = configLoading || (wouldShowAuthCard && !revalidation.done);

	return {
		authenticatedServices,
		unauthenticatedServices,
		isLoading,
		refetch,
		markOptimisticallyAuthenticated,
	};
};
